import { Chip as MuiChip, ChipProps as MuiChipProps, styled } from '@mui/material';
import React from 'react';

import { colorSystem } from '../../theme';
import { Dot } from '../Dot';
import { Flexbox } from '../Flexbox';
import { Text } from '../Text';

export interface StatusChipProps extends Omit<MuiChipProps, 'color'> {
    label: React.ReactNode;
    color: keyof typeof colorSystem;
    disabled?: boolean;
    enableHoverEffect?: boolean;
    showEllipsis?: boolean;
    overrides?: {
        Container?: React.ComponentType<MuiChipProps>;
    };
}

const commonStyle = {
    cursor: 'inherit',
    width: 'fit-content',
    height: 'fit-content',
    minHeight: 22,
    borderRadius: 24,
    border: `1px solid ${colorSystem.neutral[2]}`,
    background: colorSystem.neutral.white,
    '&.MuiChip-sizeSmall > .MuiChip-label': {
        paddingLeft: '6px',
        paddingRight: '6px',
    },
};

export const StatusChipDefaultContainer = styled(MuiChip)({
    ...commonStyle,
});

const HoverContainer = styled(MuiChip)({
    ...commonStyle,
    '&:hover': { background: colorSystem.neutral[1] },
});

interface ChipLabelProps {
    label: React.ReactNode;
    color: keyof typeof colorSystem;
    disabled: boolean;
    showEllipsis?: boolean;
}

const ChipLabel: React.FunctionComponent<ChipLabelProps> = ({ label, color, disabled, showEllipsis }) => (
    <Flexbox
        gap={4}
        sx={{
            alignItems: 'center',
        }}
    >
        <Dot color={disabled ? colorSystem[color][2] : colorSystem[color][5]} />
        <Text
            variant="body-small"
            showEllipsis={showEllipsis}
            color={disabled ? colorSystem.neutral[6] : colorSystem.neutral[8]}
        >
            {label}
        </Text>
    </Flexbox>
);

export const StatusChip = React.forwardRef<HTMLDivElement, StatusChipProps>(
    (
        { label, color, disabled = false, showEllipsis, enableHoverEffect = false, overrides = {}, ...muiChipProps },
        ref,
    ) => {
        const Container = overrides.Container || (enableHoverEffect ? HoverContainer : StatusChipDefaultContainer);

        return (
            <Container
                ref={ref}
                label={<ChipLabel label={label} color={color} disabled={disabled} showEllipsis={showEllipsis} />}
                {...muiChipProps}
            />
        );
    },
);
